/**
 * Cierre de caja.
 *
 * Es un botón, a propósito: el recepcionista termina su turno y se va. El
 * cuadre contra el efectivo lo hace después un encargado desde Historial de
 * Cajas, así que acá NO se pide contar nada.
 *
 * Lo que sí necesita saber en este momento es cuánto efectivo entregar a la
 * caja fuerte. Ese número es lo primero del resumen, y sale de la MISMA función
 * del servidor que arma la planilla de arqueo, así que no puede diferir de lo
 * que después va a ver el encargado.
 *
 * El desglose y los movimientos son los mismos componentes que usa el historial
 * (views/Reports/CashTurnBreakdown.js): un turno mirado en dos momentos tiene
 * que verse igual.
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SavingsIcon from '@mui/icons-material/Savings';
import { toast } from 'react-toastify';
import { postApi } from 'views/services/api';
import { SectionTitle, TurnBreakdown, TurnTransactions } from 'views/Reports/CashTurnBreakdown';
import { dateTime, money, numeric } from 'views/Reports/cashShared';

const CloseCashRegister = () => {
  const theme = useTheme();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClose = async () => {
    setLoading(true);
    try {
      // El hotel sale del token, no del cliente: para un empleado
      // `hotelData._id` es su propio id, no el del hotel.
      const response = await postApi('api/cashregister/close', {});

      if (response?.status === 200) {
        toast.success('Caja cerrada');
        setResult(response.data);
      } else {
        toast.error(response?.response?.data?.error || 'No se pudo cerrar la caja.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'No se pudo cerrar la caja.');
    } finally {
      setLoading(false);
    }
  };

  const register = result?.cashRegister;
  const summary = result?.summary;
  const baseCurrency = register?.baseCurrency || 'USD';
  const cashToHandOver = (result?.cashToHandOver || []).filter((row) => Number(row.expected) > 0);

  // ── Antes de cerrar ──────────────────────────────────────────────────
  if (!result) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <LockIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>
              Cerrar caja
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: '62ch' }}>
            Al cerrar se congela el corte del turno y se te muestra cuánto efectivo entregar. El cuadre contra el
            efectivo lo hace después un encargado desde Historial de Cajas.
          </Typography>

          <Button
            variant="contained"
            disableElevation
            size="large"
            sx={{ mt: 2.5, maxWidth: 460 }}
            fullWidth
            disabled={loading}
            onClick={handleClose}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
          >
            {loading ? 'Cerrando…' : 'Cerrar caja'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Resumen del turno recién cerrado ─────────────────────────────────
  const handOverTone = theme.palette.primary.main;

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2.5, md: 4 }}
            justifyContent="space-between"
            alignItems={{ md: 'flex-start' }}
          >
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontWeight: 700 }}>
                Turno cerrado
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.25 }}>
                <Typography variant="h3" fontWeight={700} sx={numeric}>
                  #{register.registerNumber}
                </Typography>
                <Chip size="small" color="warning" label="Pendiente de arqueo" />
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Apertura
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={numeric}>
                  {dateTime(register.openedAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Cierre
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={numeric}>
                  {dateTime(register.closedAt)}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            divider={<Divider orientation="vertical" flexItem />}
            spacing={{ xs: 2, sm: 0 }}
          >
            <Box sx={{ pr: { sm: 4 } }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total del turno ({baseCurrency})
              </Typography>
              <Typography variant="h2" fontWeight={700} sx={{ ...numeric, lineHeight: 1.15 }}>
                {money(summary.totalInBaseCurrency, baseCurrency)}
              </Typography>
            </Box>

            <Box sx={{ px: { sm: 4 } }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Transacciones
              </Typography>
              <Typography variant="h4" fontWeight={600} sx={{ ...numeric, lineHeight: 1.6 }}>
                {summary.totalTransactions}
              </Typography>
            </Box>

            {(register.openingFloat || []).length > 0 && (
              <Box sx={{ px: { sm: 4 } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Fondo de apertura
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {register.openingFloat.map((row) => (
                    <Typography key={row.currency} variant="body1" fontWeight={600} sx={numeric}>
                      {money(row.amount, row.currency)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Lo accionable: cuánto efectivo entregar. Es lo único que el
          recepcionista tiene que HACER con este resumen, así que va antes que
          cualquier desglose. */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 2,
          borderColor: alpha(handOverTone, 0.4),
          bgcolor: alpha(handOverTone, 0.03)
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.25 }}>
            <SavingsIcon fontSize="small" sx={{ color: handOverTone }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Efectivo a entregar
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '62ch' }}>
            Entregá todo el efectivo de la gaveta, incluido el fondo con el que abriste. El turno siguiente saca su
            propio fondo de la caja fuerte.
          </Typography>

          {cashToHandOver.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ mt: 2 }}>
              No hay efectivo que entregar: este turno no tuvo fondo ni cobros en efectivo.
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 2, maxWidth: 520 }}>
              {cashToHandOver.map((row) => (
                <Box
                  key={row.currency}
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="body2" fontWeight={700}>
                      {row.currency}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ ...numeric, color: handOverTone }}>
                      {money(row.expected, row.currency)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={numeric}>
                    fondo {money(row.float, row.currency)} + cobrado {money(row.collected, row.currency)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <SectionTitle hint="A qué se imputó el dinero y por qué medio entró.">Desglose del turno</SectionTitle>
          <TurnBreakdown summary={summary} baseCurrency={baseCurrency} />
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <SectionTitle hint="Cada cobro que entró en el turno.">Movimientos</SectionTitle>
          <TurnTransactions transactions={result.transactions} baseCurrency={baseCurrency} />
        </CardContent>
      </Card>

      <Alert severity="info">
        Este corte quedó congelado. Un encargado va a cuadrarlo contra el efectivo desde{' '}
        <strong>Reportes › Historial de Cajas</strong>, donde la caja #{register.registerNumber} ya aparece pendiente de
        arqueo.
      </Alert>
    </Box>
  );
};

export default CloseCashRegister;
